<template>
    <DForm @submit.prevent="submitForm">
        <DFormRow
            :label="__('settings.pagero_settings.form.hostname')"
            :is-required="true"
            :error="errors.hostname"
            :hint="__('settings.pagero_settings.form.hostname_hint')"
        >
            <DInput
                id="pagero-hostname"
                v-model="form.hostname"
                :placeholder="__('settings.pagero_settings.form.hostname')"
                :is-valid="isFormFieldValid('hostname')"
                type="text"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.pagero_settings.form.username')"
            :is-required="true"
            :error="errors.username"
            :hint="__('settings.pagero_settings.form.username_hint')"
        >
            <DInput
                id="pagero-client_id"
                v-model="form.username"
                :placeholder="__('settings.pagero_settings.form.username')"
                :is-valid="isFormFieldValid('username')"
                type="text"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.pagero_settings.form.password')"
            :is-required="true"
            :error="errors.password"
            :hint="__('settings.pagero_settings.form.password_hint')"
        >
            <DInput
                id="pagero-password"
                v-model="form.password"
                :placeholder="__('settings.pagero_settings.form.password')"
                :is-valid="isFormFieldValid('password')"
                type="password"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.pagero_settings.form.send_mode')"
            :is-required="true"
            :error="errors.send_mode"
            :hint="__('settings.pagero_settings.form.send_mode_hint')"
        >
            <DSelect
                id="env"
                v-model="form.send_mode"
                :options="sendMode"
            />
        </DFormRow>
        <DRow>
            <DCol sm="4" />
            <DCol sm="4">
                <div
                    v-if="connectionStatus === 'successful'"
                    class="green-text"
                >
                    {{ __('settings.jira_settings.connection_successful') }}
                </div>
                <div
                    v-if="connectionStatus === 'unsuccessful'"
                    class="red-text"
                >
                    {{ __('settings.jira_settings.connection_unsuccessful') }}
                </div>
            </DCol>
            <DCol
                sm="4"
                class="check-database-connection-btn"
            >
                <DButton
                    icon="lan-connect"
                    :label="__('settings.jira_settings.check_connection')"
                    :disabled="isInLoadingState"
                    @click="checkConnection"
                />
            </DCol>
        </DRow>
        <DFormControls
            v-if="canShowSaveButton"
            :dirty="form.isDirty"
            @submit="submitForm"
        />
        <DConnectionErrorModal
            :show-modal="connectionException !== null"
            :exception="connectionException"
            @close-modal="closeErrorCheckModal"
        />
    </DForm>
</template>

<script setup>
import { useForm } from '@inertiajs/vue3'

import { ref, watch, computed } from 'vue'
import { route } from '~@/Composables/route'
import DConnectionErrorModal from '~@/Components/Common/DConnectionErrorModal'

const props = defineProps({
    pageroSettings: {
        type: Object,
        default: () => {
            return {}
        }
    },
    env: {
        type: Array,
        default: () => {
            return []
        }
    },
    sendMode: {
        type: Array,
        default: () => { return [] }
    },
    errors: {
        type: Object,
        default: () => {
            return {}
        }
    }
})
const emit = defineEmits(['form-submitted'])
const form = useForm({
    hostname: props.pageroSettings?.hostname ?? null,
    username: props.pageroSettings?.username ?? null,
    password: props.pageroSettings?.password ?? null,
    send_mode: props.pageroSettings?.send_mode ?? null,
    postal_delivery: 'pagero'
})
const errorsData = ref(props.errors)
const connectionStatus = ref('')
const connectionException = ref(null)
const credentialsAreValid = ref(false)
const sendMode = ref(props.sendMode)
const hasFormChanged = ref(false)
const previousForm = ref(JSON.stringify(form))

watch(() => props.errors, (newErrors, oldErrors) => {
    errorsData.value = newErrors
})

watch(form, () => {
    const currentForm = JSON.stringify(form)
    hasFormChanged.value = currentForm !== previousForm.value
    previousForm.value = currentForm
}, { deep: true })

const canShowSaveButton = computed(() => {
    return credentialsAreValid.value && !hasFormChanged.value
})

function submitForm () {
    credentialsAreValid.value = false
    hasFormChanged.value = false
    emit('form-submitted', form)
}

function checkConnection () {
    errorsData.value = {}
    connectionException.value = null
    window.axios.post(route('settings.postal-delivery-settings.check-pagero', form))
        .then(function () {
            connectionStatus.value = 'successful'
            credentialsAreValid.value = true
            hasFormChanged.value = false
        })
        .catch(function (error) {
            connectionStatus.value = 'unsuccessful'
            credentialsAreValid.value = false

            if (error.response.data.errors !== undefined) {
                errorsData.value = error.response.data.errors
                for (const key in errorsData.value) {
                    if (typeof errorsData.value[key] === 'object') {
                        errorsData.value[key] = errorsData.value[key][0]
                    }
                }
            }

            if (error?.response.data.exception !== undefined) {
                connectionException.value = error.response.data.exception
            }
        })
}

function closeErrorCheckModal () {
    connectionException.value = null
}
</script>

<style lang="scss" scoped>
.check-database-connection-btn {
    text-align: right;
    padding-bottom: 25px;
}
.green-text {
    color: green;
}
.red-text {
    color: red;
}
</style>
